import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { posthog } from '@/lib/posthog';
import { useAuth } from './use-auth';
import { useConversationViews } from './use-conversation-views';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import type { ToolName } from '@arcanum-hq/shared-types';

export interface ConversationItem {
  id: string;
  title: string;
  status: string;
  modelId: string;
  modelVersion?: string;
  sessionMode: string;
  createdAt: string;
  updatedAt: string;
  forkedFromShareId?: string | null;
  forkedFromConversationId?: string | null;
  // Deal-metadata fields — cockpit-lovable-alignment Phase 2. Optional +
  // nullable because JSONB `->>` returns text (or null when missing); pre-
  // feature deals (no `dealMetadata` sub-key) return null for all 5.
  // Response-side names use irr/yoc (short aliases) — asymmetric vs request-
  // side targetIrr/targetYoc per research/phase-2.md § 11 R1. Storage retains
  // the request names (agent_state.dealMetadata.targetIrr); JSONB projection
  // re-aliases on extraction (agent_state->dealMetadata->>targetIrr named irr).
  market?: string | null;
  units?: string | null;
  basis?: string | null;
  irr?: string | null;
  yoc?: string | null;
  // Geocoded coords projected from agent_state.aerialLatLng (cockpit-lovable-
  // alignment /fix 2026-05-15). JSONB `->>` returns text; conversationToDeal
  // Number()-coerces. Null when the deal hasn't been geocoded yet (the
  // <DealPinMap> then renders the pseudoPosition fallback).
  lat?: string | null;
  lng?: string | null;
  // Version-gated pipeline fields — conversation-model-versioning Phase 5
  // Session 2 (ADR 0018). All optional/nullable for back-compat: existing
  // consumers (Builder sidebar, Solver) ignore them; the pipeline surface
  // (Session 5) consumes them after this wire-shape is live.
  //
  // `pipelineStatus` mirrors conversations.pipeline_status (TEXT+CHECK,
  // P1 Migration 2, default 'Analyzing'). `hasVersion` = `current_version_id
  // IS NOT NULL` (cheap null-column signal; avoids N+1 conversation_versions
  // read). `displayVersion` is server-resolved: accepted → current → latest
  // (ADR 0005, display-ONLY — storage pointers are never conflated).
  // String|null numerics (`irr`/`dscr`) mirror the existing `:26-30` shape —
  // consumers Number()-coerce with Number.isFinite guard (conversationToDeal
  // precedent; NOT a lib/format.ts parser).
  currentVersionId?: string | null;
  acceptedUnderwritingVersionId?: string | null;
  pipelineStatus?: import('@arcanum-hq/shared-types').PipelineStatus;
  hasVersion?: boolean;
  displayVersion?: {
    id: string;
    label: string;
    seq: number;
    units: string | null;
    basis: string | null;
    market: string | null;
    irr: string | null;
    dscr: string | null;
    staleVsCurrentModel: boolean;
  } | null;
}

export interface ConversationMessage {
  id: string;
  /**
   * `'system'` was added 2026-04-26 by in-platform-model-view Phase 4 Session 4.
   * The `/model-edit` endpoint appends `[model-view:${namedRange}]` SystemMessages
   * to the LangGraph thread; the API's `getMessages` (post-2026-04-26) surfaces
   * them, and `<ChatThread>` collapses adjacent same-namedRange entries.
   */
  role: 'human' | 'ai' | 'system';
  content: string;
  /**
   * ISO-8601 timestamp from the LangGraph message's `additional_kwargs.created_at`
   * (set by `appendSystemMessage` for SystemMessages). Used by `<ChatThread>`'s
   * 30s grouping window. Older messages may not carry it — `''` when absent.
   */
  createdAt: string;
  /**
   * Frozen point-in-time table values (`namedRange → { value }`) for a
   * `[table-edit:*]` breadcrumb. Carried from the API's `getMessages`
   * (`additional_kwargs.tableSnapshot`) → `<TableEditReceipt>` so older
   * receipts render the historical table, not the live model state.
   */
  tableSnapshot?: Record<string, { value: unknown; source?: string }>;
  toolResults?: Array<{ tool: string; result: unknown; input?: unknown }>;
  /**
   * Structured tool failures hydrated from the LangGraph checkpoint via the
   * API's `getMessages` endpoint. Phase 4 Session 1 wired the API side; this
   * field carries them through to `ChatThread` so reload re-renders the
   * `<ToolErrorCallout>` alongside any partial `toolResults`.
   */
  toolErrors?: Array<{
    tool: string;
    code: string;
    message: string;
    details?: Record<string, unknown>;
  }>;
  /**
   * Phase 2 Session 1 — derived from the existing `toolResults` + `toolErrors`
   * arrays via synthetic IDs (`legacy-${message.id}-${index}` for results,
   * `legacy-error-${message.id}-${index}` for errors). The API's `getMessages`
   * does NOT project `tool_call_id` in its public response shape (verified
   * against `apps/api/src/modules/conversations/conversations.service.v2.ts`
   * at /gap-analysis time), so the IDs are client-only and never round-trip
   * to the server. Live-stream IDs come from real Anthropic SSE events;
   * collision is impossible because synthetic IDs are bound to a hydrated
   * `message.id`.
   *
   * Hydrated `complete` entries carry `input: {}` — pre-umbrella conversations
   * never persisted tool-call args in a queryable shape, and the existing
   * renderers' `complete` branch reads from `result`, not `input`.
   *
   * Session 4's `<ChatThread>` rewrite iterates `toolCallStates` for receipt
   * grouping; pre-umbrella row hydration must populate this for parity.
   */
  toolCallStates?: Map<string, ToolCallState>;
}

// --- API response shapes (match what the backend actually returns) ---

/** GET /api/conversations → { success, data: { conversations, total, hasMore }, meta } */
interface ApiConversationListResponse {
  success: boolean;
  data: {
    conversations: ConversationItem[];
    total: number;
    hasMore: boolean;
  };
}

/** GET /api/conversations/:id → { success, data: { id, title, status, ..., agentState, uiPrefs }, meta } */
interface ApiConversationDetailResponse {
  success: boolean;
  data: {
    id: string;
    title: string;
    status: string;
    modelId: string;
    sessionMode: string;
    agentState: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
    /**
     * Per-conversation UI-only preferences (column widths, future
     * affordances). Surfaced by the API since model-view-ux-polish Phase 3
     * Session 3.2 (2026-04-27); shape mirrors shared-types `UiPrefs`. Empty
     * object on legacy rows (pre-migration) or rows where no PATCH has fired.
     * Distinct namespace from `agentState` — not echoed via Realtime.
     */
    uiPrefs?: { colWidths?: Record<string, Record<string, number>> };
    /**
     * Fork-lineage fields populated when the conversation was created via
     * `POST /api/shares/:id/fork` (external-sharing Phase 4 Session 4.3,
     * 2026-05-09). Both null on conversations created via the normal create
     * flow. `<ShareForkBanner>` (Phase 4 Session 4.3) gates on
     * `forkedFromShareId !== null`; `<ShareBackModal>` (Session 4.4) reads
     * the same field as the chain anchor for fork-back submissions.
     */
    forkedFromShareId?: string | null;
    forkedFromConversationId?: string | null;
    // NOTE: messages are NOT returned by the API. Messages live in LangGraph
    // checkpoint storage and are streamed via SSE, not REST.
  };
}

/** POST /api/conversations → { success, data: { id, title, modelId, modelVersion, sessionMode }, meta } */
interface ApiCreateConversationResponse {
  success: boolean;
  data: {
    id: string;
    title: string;
    modelId: string;
    modelVersion: string;
    sessionMode: string;
  };
}

interface CreateConversationRequest {
  title: string;
  modelId?: string;
  sessionMode?: 'guided' | 'expert';
  dealMetadata?: {
    market?: string;
    units?: number;
    basis?: string;
    targetIrr?: string;
    targetYoc?: string;
  };
}

// --- Hydration helpers ---

/**
 * Derive `toolCallStates` for a hydrated AI message. IDs are synthesized
 * client-side (`legacy-${messageId}-${index}` for results;
 * `legacy-error-${messageId}-${index}` for errors) — never round-trip to
 * the server. Pre-umbrella conversations never persisted `tool_call_id` in
 * the public response shape, so this is the parity bridge for Phase 2's
 * receipt-grouping iteration in `<ChatThread>`.
 */
export function deriveToolCallStates(
  messageId: string,
  toolResults:
    | Array<{ tool: string; result: unknown; input?: unknown }>
    | undefined,
  toolErrors:
    | Array<{
        tool: string;
        code: string;
        message: string;
        details?: Record<string, unknown>;
      }>
    | undefined
): Map<string, ToolCallState> {
  const states = new Map<string, ToolCallState>();
  (toolResults ?? []).forEach((tr, idx) => {
    states.set(`legacy-${messageId}-${idx}`, {
      phase: 'complete',
      tool: tr.tool as ToolName,
      // Restore the persisted tool-call args (getMessages now projects them)
      // so input-rendering cards (propose_table_fill) rehydrate on reload
      // instead of falling back to a skeleton. (table-input-ux history, 2026-06-08.)
      input: (tr.input ?? {}) as Record<string, unknown>,
      result: tr.result,
    });
  });
  (toolErrors ?? []).forEach((te, idx) => {
    states.set(`legacy-error-${messageId}-${idx}`, {
      phase: 'errored',
      tool: te.tool as ToolName,
      input: null,
      error: { code: te.code, message: te.message, details: te.details },
    });
  });
  return states;
}

// --- Hooks ---

/**
 * List conversations. Normalizes the API's `{ conversations: [...] }` wrapper
 * into a flat array for component consumption.
 */
export function useConversations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.conversations.list(),
    queryFn: async (): Promise<{ data: ConversationItem[]; total: number }> => {
      const res = await api.get<ApiConversationListResponse>(
        '/api/conversations'
      );
      // API wraps the list in res.data.conversations
      const list = res.data;
      return {
        data: Array.isArray(list?.conversations) ? list.conversations : [],
        total: list?.total ?? 0,
      };
    },
    // Don't fire on unauthenticated /builder visits — avoids benign 401s +
    // console-error noise. (builder-unauth-query-gate, 2026-05-16)
    enabled: !!user,
  });
}

/**
 * Get a single conversation detail with agent state.
 *
 * THIN SHIM (builder-ssot-consolidation Phase 5 Session 5.2; ADR 0017):
 * Keeps the DUAL fetch (`/conversations/:id` + `/messages`) for
 * agentState/uiPrefs/messages. OVERLAYS the `inputs`-derived maps from
 * `useConversationViews` on top of `agentState` so consumers reading
 * `agentState.inputValues` + `agentState.inputProvenance` get the SSOT
 * values from `/views`. Per ADR 0017, messages/uiPrefs/hydration still ride
 * the existing detail+messages path — those are NOT in `/views`.
 *
 * Three reads: detail + messages (detail cache key) + views (views cache key).
 * The overlay sets:
 *   - agentState.inputValues ← value-map from views.inputs[k].value
 *   - agentState.inputProvenance ← views.inputs (the records — ADR 0018)
 *   - agentState.dealMetadata ← views.dealMetadata
 *   - agentState.aerialLatLng ← views.aerialLatLng
 */
export function useConversation(id: string | null) {
  // Dual-fetch: detail + messages. Unchanged from pre-shim.
  const detailQuery = useQuery({
    queryKey: queryKeys.conversations.detail(id || ''),
    queryFn: async () => {
      type ApiMessage = {
        role: string;
        content: string;
        /**
         * ISO-8601 timestamp from the LangGraph message's
         * `additional_kwargs.created_at`. Surfaced by `getMessages` for
         * SystemMessages appended via `appendSystemMessage` (in-platform-
         * model-view Phase 2/4). Older messages (pre-2026-04-26) won't
         * carry it — caller falls back to `''`.
         */
        created_at?: string;
        /**
         * Frozen `namedRange → { value }` snapshot for `[table-edit:*]`
         * breadcrumbs (from `additional_kwargs.tableSnapshot`). Lets the
         * receipt render the point-in-time table. Absent on legacy receipts.
         */
        tableSnapshot?: Record<string, { value: unknown; source?: string }>;
        toolResults?: Array<{ tool: string; result: unknown; input?: unknown }>;
        toolErrors?: Array<{
          tool: string;
          code: string;
          message: string;
          details?: Record<string, unknown>;
        }>;
      };
      const [detailRes, messagesRes] = await Promise.all([
        api.get<ApiConversationDetailResponse>(`/api/conversations/${id}`),
        api
          .get<{ success: boolean; data: ApiMessage[] }>(
            `/api/conversations/${id}/messages`
          )
          .catch(() => ({ data: [] as ApiMessage[] })),
      ]);
      // Map checkpoint messages to ConversationMessage format; carry through
      // tool_result payloads so ChatThread can re-render MetricsCard / etc.
      // Phase 4 Session 2: also carry through toolErrors[] so reload re-renders
      // the <ToolErrorCallout> alongside any partial toolResults.
      // Phase 2 Session 1: also derive toolCallStates from the toolResults +
      // toolErrors arrays via synthetic IDs — Session 4's <ChatThread> rewrite
      // iterates this Map for receipt grouping. Pre-umbrella rows had no
      // tool_call_id in the persisted shape; synthesizing client-side IDs
      // preserves parity without an API surface change.
      const messages: ConversationMessage[] = (messagesRes.data ?? []).map(
        (m, i) => {
          const msgId = `hist-${i}`;
          return {
            id: msgId,
            // Widen to include `'system'` — Phase 4 Session 4. Defensive cast
            // catches anything outside the union (LangGraph could in theory
            // surface other kinds, though `getMessages` filters to these three).
            role: (m.role === 'human' || m.role === 'ai' || m.role === 'system'
              ? m.role
              : 'ai') as 'human' | 'ai' | 'system',
            content: m.content,
            // `created_at` arrives as a snake_case field on the API response (the
            // endpoint mirrors LangGraph's `additional_kwargs` field name).
            createdAt: typeof m.created_at === 'string' ? m.created_at : '',
            tableSnapshot: m.tableSnapshot,
            toolResults: m.toolResults,
            toolErrors: m.toolErrors,
            toolCallStates: deriveToolCallStates(
              msgId,
              m.toolResults,
              m.toolErrors
            ),
          };
        }
      );
      return {
        data: {
          ...detailRes.data,
          messages,
        },
      };
    },
    enabled: !!id,
    // Always refetch when the route remounts onto a conversation. The
    // alternative — serving a stale cache entry that was populated when
    // the conversation had 0 messages — produces an empty chat panel for a
    // conversation that has 5+ persisted messages on the server. Surfaced
    // 2026-05-06 after streaming done refetch races (see use-streaming-v2's
    // matching `refetchQueries` switch). The cost is one extra network
    // round-trip per Builder mount; the alternative is a class of "where
    // did my chat history go?" bugs that survive the cache without a
    // hard reload.
    refetchOnMount: 'always',
  });

  // Third read: SSOT projection. If views resolves, overlay inputs-derived
  // maps onto agentState. If views is still loading/errored, serve the
  // detail-query agentState as-is (graceful degradation). ADR 0017.
  const viewsQuery = useConversationViews(id);

  // Overlay the inputs-derived maps when views data is available.
  // We return the detail query's shape but with agentState patched.
  if (detailQuery.data && viewsQuery.data) {
    const views = viewsQuery.data;
    const currentAgentState = detailQuery.data.data.agentState ?? {};

    // Reconstitute inputValues (value-map) and inputProvenance (records map)
    // from views.inputs[k] (ADR 0018). The optimistic-patch KEEP site writes
    // into agentState.inputValues directly — the overlay here will be
    // superseded by the next cache invalidation + refetch cycle, which is
    // correct (the patch is a transient cache write, not persisted state).
    const inputValues: Record<string, unknown> = Object.fromEntries(
      Object.entries(views.inputs).map(([k, r]) => [k, r.value])
    );
    const inputProvenance: Record<string, unknown> = views.inputs;

    const overlaidAgentState: Record<string, unknown> = {
      ...currentAgentState,
      inputValues,
      inputProvenance,
      // Overlay dealMetadata and aerialLatLng from SSOT when present.
      ...(views.dealMetadata !== undefined
        ? { dealMetadata: views.dealMetadata }
        : {}),
      ...(views.aerialLatLng !== undefined
        ? { aerialLatLng: views.aerialLatLng }
        : {}),
    };

    return {
      ...detailQuery,
      data: {
        data: {
          ...detailQuery.data.data,
          agentState: overlaidAgentState,
        },
      },
    };
  }

  return detailQuery;
}

/**
 * Create a new conversation. Normalizes the partial API response into a
 * full ConversationItem with sensible defaults for missing fields.
 */
export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CreateConversationRequest
    ): Promise<{ data: ConversationItem }> => {
      const res = await api.post<ApiCreateConversationResponse>(
        '/api/conversations',
        data
      );
      const created = res.data;
      // API returns { id, title, modelId, modelVersion, sessionMode } — fill in defaults for rest
      return {
        data: {
          id: created.id,
          title: created.title,
          status: 'active',
          modelId: created.modelId,
          modelVersion: created.modelVersion,
          sessionMode: created.sessionMode,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      // NEW Phase 3 § 3.4 — PostHog product event (ADR 0005: fires from this
      // mutation onSuccess — the canonical chokepoint for all creation paths).
      posthog.capture('conversation_created', { conversation_id: res.data.id });
      // Geocode-on-create-market tiered refetch (cockpit-lovable-alignment
      // /fix 2026-05-15). The server fires `geocodeAndCache` fire-and-forget
      // AFTER the create response, so the deal initially comes back with
      // `aerialLatLng: null` and the Cockpit `<DealPinMap>` renders the
      // UUID-seeded `pseudoPosition` fallback. There is NO Realtime broadcast
      // on the geocoder's raw admin UPDATE, so the cache won't self-refresh.
      // Mirror the proven `use-streaming-v2.ts:691-696` 5s+30s tiered-refetch
      // idiom (shipped `external-sharing` Phase 6 dev-smoke 2026-05-09),
      // keyed to `conversations.all` (the Cockpit list query) instead of
      // `.detail`. 5s catches local-dev (~1-2s geocode) + Cloud Run warm
      // (~3-5s); 30s catches the Cloud Run cold-start tail. The pin snaps
      // pseudoPosition → real coords at ≈5s with NO manual refresh. Cost:
      // ≤2 extra list GETs that return identical data when the market was
      // unparseable (typo / non-US / nonexistent → `aerialLatLng` stays
      // null → pin stays at pseudoPosition; documented-acceptable, same as
      // the streamMessage pattern). Long-term fix = server Realtime
      // broadcast (backlog `external-sharing-aerial-realtime-broadcast`).
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: queryKeys.conversations.all });
      }, 5000);
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: queryKeys.conversations.all });
      }, 30000);
    },
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.put(`/api/conversations/${id}`, { title }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(vars.id),
      });
    },
  });
}

export function useForkConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      targetVersion,
    }: {
      conversationId: string;
      targetVersion: string;
    }) =>
      api.post<{
        data: {
          id: string;
          title: string;
          modelId: string;
          modelVersion: string;
          orphanedInputCount: number;
        };
      }>(`/api/conversations/${conversationId}/fork`, { targetVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    },
  });
}

/**
 * Manual in-place upgrade of a conversation to the latest model version.
 * Unlike fork (which creates a NEW conversation), this keeps the SAME
 * conversation + chat history. Invalidates the conversation + its `/views`
 * projection so the VariablesPanel re-renders with the carried/orphaned split.
 */
export function useUpgradeModelVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      api.post<{
        data: {
          fromVersion: string;
          toVersion: string;
          orphanedCount: number;
          alreadyLatest: boolean;
        };
      }>(`/api/conversations/${conversationId}/upgrade-model-version`, {}),
    onSuccess: (_data, conversationId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.views(conversationId),
      });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/conversations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    },
  });
}
